<template>
    <div class="ppv2-space-y-4">
        <InfoMessageBox class="ppv2-mt-2" :title="$t('messageBox.loyaltyProgram.start.title')" color="info" />
        <div class="ppv2-flex ppv2-flex-col ppv2-space-y-4">
            <div class="ppv2-relative ppv2-p-4 ppv2-cursor-pointer">
                <div class="ppv2-absolute ppv2-top-2 ppv2-right-2">
                    <BaseSwitch v-model="formData.is_loyalty_program_enabled" required @change="onLoyaltyToggle" />
                </div>
                <div>
                    <div class="ppv2-text-base ppv2-text-gray-800 poppinsMedium">
                        <BaseLabel :label="$t('loyaltyProgram.fields.enableLoyaltyProgram.label')" />
                    </div>
                    <div class="ppv2-flex ppv2-items-center ppv2-space-x-2 ppv2-mt-1 ppv2-text-xs ppv2-text-gray-500">
                        <BaseParagraphe>
                            {{ $t('loyaltyProgram.fields.enableLoyaltyProgram.description') }}
                        </BaseParagraphe>
                    </div>
                    <div v-if="formData.is_loyalty_program_enabled">
                        <BaseInput
                            v-model="formData.points_for_one_euro"
                            :label="$t('loyaltyProgram.fields.pointsPerEuro.label', { symbol: $symbolCurrency })"
                            class="ppv2-mt-5"
                            type="number"
                            min="1"
                            step="1"
                            placeholder="Ex:10"
                            :tooltipMsg="$t('loyaltyProgram.fields.pointsPerEuro.infoMessage', { symbol: $symbolCurrency })"
                            required
                        >
                            <template #leftIcon>
                                <svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path
                                        d="M4 9.5H20M4 15.5H20M10 3.5L8 21.5M16 3.5L14 21.5"
                                        stroke="black"
                                        stroke-width="2"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                    />
                                </svg>
                            </template>
                        </BaseInput>
                        <BaseInput
                            v-model="formData.fidelity_points_trans"
                            :label="$t('loyaltyProgram.fields.pointsValue.label')"
                            class="ppv2-mt-5"
                            type="number"
                            min="1"
                            step="1"
                            placeholder="100"
                            :tooltipMsg="$t('loyaltyProgram.fields.pointsValue.infoMessage', { symbol: $symbolCurrency })"
                            required
                        >
                            <template #leftIcon>
                                <svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path
                                        d="M4 9.5H20M4 15.5H20M10 3.5L8 21.5M16 3.5L14 21.5"
                                        stroke="black"
                                        stroke-width="2"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                    />
                                </svg>
                            </template>
                        </BaseInput>
                        <BaseInput
                            v-model="formData.loyalty_auto_transfer_threshold"
                            :label="$t('loyaltyProgram.fields.autoTransferThreshold.label')"
                            class="ppv2-mt-5"
                            type="number"
                            min="0"
                            step="1"
                            placeholder="1000"
                            :tooltipMsg="$t('loyaltyProgram.fields.autoTransferThreshold.infoMessage')"
                            required
                        >
                            <template #leftIcon>
                                <svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path
                                        d="M4 9.5H20M4 15.5H20M10 3.5L8 21.5M16 3.5L14 21.5"
                                        stroke="black"
                                        stroke-width="2"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                    />
                                </svg>
                            </template>
                        </BaseInput>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import InfoMessageBox from '../../../Shared/InfoMessageBox.vue'
import BaseSwitch from '../../../Shared/BaseSwitch.vue'
import BaseLabel from '../../../Shared/BaseLabel.vue'
import BaseParagraphe from '../../../Shared/BaseParagraphe.vue'
import BaseInput from '../../../Shared/BaseInput.vue'

// Valeurs proposées à la (ré)activation du programme, alignées sur les `default()` des
// migrations et les placeholders des champs. Le serveur refuse 0 sur les deux ratios quand
// le programme est actif : sans amorçage, rouvrir le panneau donnerait un 422 immédiat.
const LOYALTY_DEFAULTS = {
    points_for_one_euro: 10,
    fidelity_points_trans: 100,
    loyalty_auto_transfer_threshold: 0, // 0 = transfert automatique désactivé
}

export default {
    name: 'LoyaltyProgram',
    components: {
        InfoMessageBox,
        BaseSwitch,
        BaseLabel,
        BaseParagraphe,
        BaseInput,
    },
    data() {
        return {
            formData: {
                is_loyalty_program_enabled: false,
                points_for_one_euro: null,
                fidelity_points_trans: null,
                loyalty_auto_transfer_threshold: null,
            },
        }
    },
    computed: {
        establishment() {
            return this.$store.getters['establishment/establishment']
        },
    },
    watch: {
        establishment: {
            handler() {
                this.initializeForm()
            },
            immediate: true,
            deep: true,
        },
        formData: {
            handler(newVal) {
                this.$emit('update', newVal)
            },
            deep: true,
        },
    },
    methods: {
        // Miroir de la normalisation serveur (EstablishmentRequest::prepareForValidation).
        // Le serveur reste la source de vérité — ceci évite seulement de renvoyer des valeurs
        // orphelines et de se prendre un 422 en rouvrant le panneau.
        // On écoute `change` et non un watcher : BaseSwitch ne l'émet que sur clic utilisateur,
        // donc initializeForm() (watcher immediate+deep) ne peut pas écraser les valeurs en base.
        onLoyaltyToggle(enabled) {
            if (!enabled) {
                Object.keys(LOYALTY_DEFAULTS).forEach((field) => {
                    this.formData[field] = 0
                })
                return
            }
            Object.keys(LOYALTY_DEFAULTS).forEach((field) => {
                if (!Number(this.formData[field])) this.formData[field] = LOYALTY_DEFAULTS[field]
            })
        },
        initializeForm() {
            const fields = [
                'is_loyalty_program_enabled',
                'points_for_one_euro',
                'fidelity_points_trans',
                'loyalty_auto_transfer_threshold',
            ]
            fields.forEach((field) => {
                if (this.establishment.hasOwnProperty(field)) {
                    this.formData[field] = this.establishment[field]
                }
            })
        },
    },
}
</script>

<style scoped></style>
